// @tennis-matches/services/app-manifests
// Loads app manifests from Supabase (hybrid: DB primary, code fallback)

import { getSupabaseClient } from "../supabase/index.js";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
/** Obergrenze für die Manifest-Liste; bei Mobile/kalter Verbindung war 2500 ms oft zu knapp → Fallback + Warnung. */
const APP_MANIFESTS_TIMEOUT_MS = 6000;
let __manifestsCache = null;
let __manifestsCacheTimestamp = 0;
let __manifestsInflight = null;
let __manifestsAbortWarned = false;
let __preferAppManifestsRpc = null;

function __isAbortError(err) {
  const name = String(err?.name || "").toLowerCase();
  const msg = String(err?.message || "").toLowerCase();
  return name === "aborterror" || msg.includes("aborted");
}

function __isAppConfigSchemaUnavailable(error) {
  if (!error) return false;
  const code = String(error.code || "");
  const status = Number(error.status || error.statusCode || 0);
  const msg = String(error.message || "").toLowerCase();
  return (
    code === "PGRST106" ||
    status === 406 ||
    msg.includes("schema must be") ||
    msg.includes("invalid schema")
  );
}

function __resolveSupabaseTarget() {
  if (typeof globalThis !== "undefined") {
    const ttb = String(globalThis.__TTB_ENV__?.TM_SUPABASE_TARGET || "").toLowerCase();
    if (ttb) return ttb;
  }
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return String(
      import.meta.env.VITE_TM_SUPABASE_TARGET || import.meta.env.TM_SUPABASE_TARGET || ""
    ).toLowerCase();
  }
  return "";
}

function __shouldPreferAppManifestsRpc() {
  if (__preferAppManifestsRpc === true) return true;
  const target = __resolveSupabaseTarget();
  if (target === "test" || target === "staging" || target === "preview") {
    __preferAppManifestsRpc = true;
    return true;
  }
  return false;
}

function __rpcActiveAppManifests(supabase, ctrl) {
  let q = supabase.rpc("list_active_app_manifests");
  if (ctrl && typeof q?.abortSignal === "function") {
    q = q.abortSignal(ctrl.signal);
  }
  return q;
}

function __buildActiveManifestsQuery(db, tableName, ctrl) {
  let q = db
    .from(tableName)
    .select("*")
    .eq("active", true)
    .order("app_id");
  if (ctrl && typeof q?.abortSignal === "function") {
    q = q.abortSignal(ctrl.signal);
  }
  return q;
}

async function __fetchActiveAppManifests(supabase, ctrl) {
  if (__shouldPreferAppManifestsRpc()) {
    return __rpcActiveAppManifests(supabase, ctrl);
  }

  if (supabase?.schema && typeof supabase.schema === "function") {
    const primary = await __buildActiveManifestsQuery(
      supabase.schema("app_config"),
      "app_manifests",
      ctrl
    );
    if (!primary.error) {
      return primary;
    }
    if (__isAppConfigSchemaUnavailable(primary.error)) {
      __preferAppManifestsRpc = true;
      return __rpcActiveAppManifests(supabase, ctrl);
    }
    return primary;
  }

  return __rpcActiveAppManifests(supabase, ctrl);
}

function __withTimeout(promise, ms = APP_MANIFESTS_TIMEOUT_MS) {
  const timeoutMs = Math.max(1, Number(ms || APP_MANIFESTS_TIMEOUT_MS));
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve({ __timeout__: true }), timeoutMs);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * Loads all active app manifests from Supabase
 * @returns {Promise<{ok: boolean, manifests?: Array<Object>, reason?: string}>}
 */
export async function loadAppManifests() {
  // Check cache first
  if (__manifestsCache && __manifestsCacheTimestamp) {
    const age = Date.now() - __manifestsCacheTimestamp;
    if (age < CACHE_TTL_MS) {
      return { ok: true, manifests: __manifestsCache };
    }
  }
  if (__manifestsInflight) {
    return await __manifestsInflight;
  }

  const __dev =
    typeof import.meta !== "undefined" && !!import.meta.env?.DEV && typeof performance !== "undefined";
  const __m = __dev ? `tm-app-manifests-db-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` : "";
  if (__dev) {
    try {
      performance.mark(`${__m}:start`);
    } catch (_) {}
  }

  __manifestsInflight = (async () => {
    try {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, reason: "no-supabase-client" };
    }

    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer =
      ctrl && typeof setTimeout === "function"
        ? setTimeout(() => {
            try {
              ctrl.abort();
            } catch (_) {}
          }, APP_MANIFESTS_TIMEOUT_MS)
        : null;

    const t0 = __dev ? performance.now() : 0;
    const raced = await __withTimeout(__fetchActiveAppManifests(supabase, ctrl), APP_MANIFESTS_TIMEOUT_MS);
    if (raced && raced.__timeout__ === true) {
      if (timer) clearTimeout(timer);
      if (!__manifestsAbortWarned) {
        __manifestsAbortWarned = true;
        console.warn("[app-manifests] Supabase query timeout, using fallback cache/code.");
      }
      return { ok: false, reason: "timeout-or-abort" };
    }
    const { data, error } = raced || {};
    if (timer) clearTimeout(timer);
    if (__dev) {
      try {
        performance.mark(`${__m}:end`);
        performance.measure(__m, `${__m}:start`, `${__m}:end`);
      } catch (_) {}
      const ms = performance.now() - t0;
      console.debug("[perf] loadAppManifests Supabase query", { ms: Math.round(ms * 10) / 10 });
    }

    if (error) {
      if (__isAbortError(error)) {
        if (!__manifestsAbortWarned) {
          __manifestsAbortWarned = true;
          console.warn("[app-manifests] Supabase query aborted/timeout, using fallback cache/code.");
        }
        return { ok: false, reason: "timeout-or-abort", error };
      }
      console.warn("[app-manifests] Failed to load from Supabase:", error);
      return { ok: false, reason: "db-error", error };
    }

    // Transform DB rows to manifest format
    const manifests = (data || []).map((row) => ({
      id: row.app_id,
      titleKey: row.title_key,
      routes: Array.isArray(row.routes) ? row.routes : [],
      breadcrumbSteps: Array.isArray(row.breadcrumb_steps) ? row.breadcrumb_steps : [],
      nav: Array.isArray(row.nav) ? row.nav : [],
      flowGeneratorType: row.flow_generator_type || null,
      flowGeneratorConfig: row.flow_generator_config || {},
      tenantId: row.tenant_id || null,
    }));

    // Update cache
    __manifestsCache = manifests;
    __manifestsCacheTimestamp = Date.now();

    return { ok: true, manifests };
  } catch (e) {
    if (__isAbortError(e)) {
      if (!__manifestsAbortWarned) {
        __manifestsAbortWarned = true;
        console.warn("[app-manifests] Supabase query aborted/timeout, using fallback cache/code.");
      }
      return { ok: false, reason: "timeout-or-abort", error: e };
    }
    console.warn("[app-manifests] Exception loading from Supabase:", e);
    return { ok: false, reason: "exception", error: e };
  } finally {
      __manifestsInflight = null;
    }
  })();

  return await __manifestsInflight;
}

/**
 * Synchronously gets cached manifests (if available)
 * @returns {Array<Object>|null}
 */
export function getCachedManifests() {
  if (__manifestsCache && __manifestsCacheTimestamp) {
    const age = Date.now() - __manifestsCacheTimestamp;
    if (age < CACHE_TTL_MS) {
      return __manifestsCache;
    }
  }
  return null;
}

/**
 * Clears the manifest cache (useful for testing or forced refresh)
 */
export function clearManifestCache() {
  __manifestsCache = null;
  __manifestsCacheTimestamp = 0;
}
